import { formatInTimeZone } from 'date-fns-tz';
import {
    ChevronLeft,
    ChevronRight,
    Loader2,
    Mic,
    Plus,
    SendHorizontal,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    AvailabilityFormDialog,
    type EditTarget,
} from '@apps/web/components/caregivers/availability_form_dialog';
import {
    AvailabilityLegend,
    AvailabilityMonthGrid,
} from '@apps/web/components/caregivers/caregiver_availability_month_grid';
import {
    TimeboxedScheduleList,
    WeeklyScheduleList,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_list';
import {
    buildTimeboxedDayGroups,
    buildWeeklyDayGroups,
    mergeTimeboxedAvailability,
    mergeWeeklyAvailability,
    minuteOfWeekToDayKey,
    minuteOfWeekToTime,
    type TimeboxedAvailabilityEntry,
    type TimelineItem,
    type WeeklyAvailabilityEntry,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_model';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { SegmentedControl } from '@apps/web/components/ui/segmented_control';
import { Textarea } from '@apps/web/components/ui/textarea';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { useVoiceInput } from '@apps/web/hooks/use_voice_input';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    addMonthsToDateKey,
    endOfMonthDateKey,
    formatDateKey,
    formatMonthKey,
    mondayStartDateKey,
    shiftDateKey,
    startOfMonthDateKey,
    toDateKey,
} from '@apps/web/lib/date_utils';
import { useCaregiverAvailabilityProfile } from '@apps/web/swr_data_fetching/data_fetching_hooks';

type CaregiverAvailabilityProfileProps = {
    readonly organizationId: string | null;
    readonly caregiverId: string | null;
    readonly externalSourceLabel?: string;
};

type AvailabilityViewMode = 'calendar' | 'one-time' | 'weekly';

export function CaregiverAvailabilityProfile({
    organizationId,
    caregiverId,
    externalSourceLabel,
}: CaregiverAvailabilityProfileProps) {
    const { timezone, isLoading: isTimezoneLoading } =
        useOrganizationTimezone();
    const [viewMode, setViewMode] = useState<AvailabilityViewMode>('calendar');
    // Null until the user navigates; the visible period then follows today.
    const [anchorOverrideKey, setAnchorOverrideKey] = useState<string | null>(
        null,
    );
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<EditTarget | undefined>(
        undefined,
    );
    const [instruction, setInstruction] = useState('');
    const [isParsing, setIsParsing] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<{
        readonly type: 'one-time' | 'weekly';
        readonly id: string;
    } | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [expandedRows, setExpandedRows] = useState<ReadonlySet<string>>(
        () => new Set(),
    );
    const isTimezoneReady = !isTimezoneLoading && !!timezone;

    const todayKey = useMemo(
        () => (isTimezoneReady ? toDateKey(new Date(), timezone) : null),
        [isTimezoneReady, timezone],
    );

    // The anchor stays fixed across mode switches so the visible period
    // keeps containing the same dates (week -> its month, month -> the week
    // of its 1st).
    const anchorDateKey = anchorOverrideKey ?? todayKey;

    const rangeStartDateKey = useMemo(() => {
        if (!anchorDateKey) {
            return null;
        }
        return viewMode === 'calendar'
            ? startOfMonthDateKey(anchorDateKey)
            : mondayStartDateKey(anchorDateKey);
    }, [anchorDateKey, viewMode]);

    const rangeEndDateKey = useMemo(() => {
        if (!rangeStartDateKey) {
            return null;
        }
        return viewMode === 'calendar'
            ? endOfMonthDateKey(rangeStartDateKey)
            : shiftDateKey(rangeStartDateKey, 6);
    }, [rangeStartDateKey, viewMode]);

    // Weekly-pattern time labels need a concrete reference week even in
    // month mode; use the week containing the visible range's start.
    const referenceWeekStartKey = useMemo(
        () =>
            rangeStartDateKey ? mondayStartDateKey(rangeStartDateKey) : null,
        [rangeStartDateKey],
    );

    useEffect(() => {
        setExpandedRows(() => new Set());
    }, [rangeStartDateKey, viewMode]);

    const {
        data,
        isLoading: isProfileLoading,
        error,
        mutate,
    } = useCaregiverAvailabilityProfile(
        organizationId,
        caregiverId,
        rangeStartDateKey,
        rangeEndDateKey,
    );

    const isLoading = isTimezoneLoading || isProfileLoading;

    const timeboxedItems = useMemo(() => {
        if (!data) {
            return [];
        }

        const items: Array<TimelineItem> = [
            ...mergeTimeboxedAvailability(
                data.availability,
                externalSourceLabel,
            ),
        ];

        data.shifts.forEach((shift) => {
            const detailParts = [shift.patient_name];
            if (shift.role_required) {
                detailParts.push(shift.role_required.toUpperCase());
            }
            items.push({
                kind: 'shift',
                id: `shift-${shift.id}`,
                type: 'shift',
                startTime: shift.shift_start_time,
                endTime: shift.shift_end_time,
                details: detailParts.join(' | '),
            });
        });

        return items.sort(
            (a, b) =>
                new Date(a.startTime).getTime() -
                new Date(b.startTime).getTime(),
        );
    }, [data, externalSourceLabel]);

    const weeklyItems = useMemo(() => {
        if (!data || !referenceWeekStartKey) {
            return [];
        }

        return mergeWeeklyAvailability(
            data.weekly_availability,
            externalSourceLabel,
            referenceWeekStartKey,
            timezone,
        );
    }, [data, externalSourceLabel, timezone, referenceWeekStartKey]);

    const timeboxedDayGroups = useMemo(
        () => buildTimeboxedDayGroups(timeboxedItems, timezone),
        [timeboxedItems, timezone],
    );

    const weeklyDayGroups = useMemo(() => {
        if (!referenceWeekStartKey) {
            return [];
        }
        return buildWeeklyDayGroups(
            weeklyItems,
            referenceWeekStartKey,
            timezone,
        );
    }, [timezone, referenceWeekStartKey, weeklyItems]);

    const rangeLabel = useMemo(() => {
        if (!rangeStartDateKey || !rangeEndDateKey) {
            return viewMode === 'calendar' ? 'Month' : 'Week';
        }
        if (viewMode === 'calendar') {
            return formatMonthKey(rangeStartDateKey, timezone);
        }
        if (viewMode === 'weekly') {
            return 'Recurring schedule';
        }
        return `${formatDateKey(rangeStartDateKey, timezone)} - ${formatDateKey(rangeEndDateKey, timezone)}`;
    }, [rangeEndDateKey, rangeStartDateKey, timezone, viewMode]);

    const handleViewModeChange = useCallback(
        (nextViewMode: AvailabilityViewMode) => {
            setViewMode(nextViewMode);
        },
        [],
    );

    const handleNavigate = useCallback(
        (direction: -1 | 1) => {
            setAnchorOverrideKey((current) => {
                const startKey = current ?? rangeStartDateKey;
                if (!startKey) {
                    return current;
                }
                return viewMode === 'calendar'
                    ? addMonthsToDateKey(
                          startOfMonthDateKey(startKey),
                          direction,
                      )
                    : shiftDateKey(mondayStartDateKey(startKey), direction * 7);
            });
        },
        [rangeStartDateKey, viewMode],
    );

    const handleAdd = useCallback(() => {
        setEditTarget(undefined);
        setDialogOpen(true);
    }, []);

    const toggleExpandedRow = useCallback((rowId: string) => {
        setExpandedRows((current) => {
            const next = new Set(current);
            if (next.has(rowId)) {
                next.delete(rowId);
            } else {
                next.add(rowId);
            }
            return next;
        });
    }, []);

    const handleEditOneTime = useCallback(
        (entry: TimeboxedAvailabilityEntry) => {
            setEditTarget({
                type: 'one-time',
                id: entry.id,
                status: entry.status,
                startDate: formatInTimeZone(
                    entry.startTime,
                    timezone,
                    'yyyy-MM-dd',
                ),
                startTime: formatInTimeZone(entry.startTime, timezone, 'HH:mm'),
                endDate: formatInTimeZone(
                    entry.endTime,
                    timezone,
                    'yyyy-MM-dd',
                ),
                endTime: formatInTimeZone(entry.endTime, timezone, 'HH:mm'),
            });
            setDialogOpen(true);
        },
        [timezone],
    );

    const handleEditWeekly = useCallback((entry: WeeklyAvailabilityEntry) => {
        const endTime = minuteOfWeekToTime(entry.endMinuteOfWeek, {
            isEndTime: true,
        });
        setEditTarget({
            type: 'weekly',
            id: entry.id,
            status: entry.status,
            dayOfWeek: minuteOfWeekToDayKey(entry.startMinuteOfWeek),
            startTime: minuteOfWeekToTime(entry.startMinuteOfWeek),
            endTime,
        });
        setDialogOpen(true);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!organizationId || !caregiverId || !deleteTarget) {
            return;
        }
        setIsDeleting(true);
        try {
            if (deleteTarget.type === 'one-time') {
                await getApiClient().delete({
                    url: '/organizations/{organization_id}/caregivers/{caregiver_id}/availability/{availability_id}',
                    pathParams: {
                        organization_id: organizationId,
                        caregiver_id: caregiverId,
                        availability_id: deleteTarget.id,
                    },
                });
                toast.success('Availability deleted', {
                    persistNotification: false,
                });
            } else {
                await getApiClient().delete({
                    url: '/organizations/{organization_id}/caregivers/{caregiver_id}/weekly-availability/{weekly_availability_id}',
                    pathParams: {
                        organization_id: organizationId,
                        caregiver_id: caregiverId,
                        weekly_availability_id: deleteTarget.id,
                    },
                });
                toast.success('Weekly availability deleted', {
                    persistNotification: false,
                });
            }
            mutate();
        } catch (error) {
            toast.error(
                error instanceof Error && error.message
                    ? error.message
                    : 'Failed to delete availability',
                { persistNotification: false },
            );
        } finally {
            setIsDeleting(false);
            setDeleteTarget(null);
        }
    }, [organizationId, caregiverId, deleteTarget, mutate]);

    const handleParse = useCallback(async () => {
        if (!organizationId || !caregiverId || isParsing) {
            return;
        }
        const trimmed = instruction.trim();
        if (!trimmed) {
            return;
        }
        setIsParsing(true);
        try {
            const response = await getApiClient().post({
                url: '/organizations/{organization_id}/caregivers/{caregiver_id}/availability/parse',
                pathParams: {
                    organization_id: organizationId,
                    caregiver_id: caregiverId,
                },
                body: { instruction: trimmed },
            });
            setInstruction('');
            toast.success(response.summary, { persistNotification: false });
            mutate();
        } catch (error) {
            toast.error(
                error instanceof Error && error.message
                    ? error.message
                    : 'Failed to parse availability instruction',
                { persistNotification: false },
            );
        } finally {
            setIsParsing(false);
        }
    }, [organizationId, caregiverId, isParsing, instruction, mutate]);

    const {
        canRecordAudio,
        isRecording,
        isTranscribing,
        voiceInputError,
        startRecording,
        stopRecording,
    } = useVoiceInput({
        organizationId: organizationId ?? '',
        currentText: instruction,
        onTranscribed: setInstruction,
        composer: 'caregiver_availability',
        voiceInputFlow: 'caregiver_availability_instruction',
        disabled: !organizationId || !caregiverId || isParsing,
    });

    const hasIds = !!organizationId && !!caregiverId;

    return (
        <section className="space-y-3">
            <header className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="flex min-h-8 items-center text-base font-semibold">
                        Availability
                    </h2>
                    <SegmentedControl
                        options={[
                            { value: 'calendar', label: 'Calendar' },
                            { value: 'one-time', label: 'One-time' },
                            { value: 'weekly', label: 'Weekly pattern' },
                        ]}
                        value={viewMode}
                        onValueChange={handleViewModeChange}
                        size="sm"
                        aria-label="Availability view mode"
                    />
                </div>
                {viewMode !== 'weekly' ? (
                    <div className="flex items-center gap-0.5">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="size-6"
                            onClick={() => handleNavigate(-1)}
                            disabled={!rangeStartDateKey}
                            aria-label={`Previous ${viewMode === 'calendar' ? 'month' : 'week'}`}
                        >
                            <ChevronLeft className="size-3.5" />
                        </Button>
                        <span className="text-xs font-medium">
                            {rangeLabel}
                        </span>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="size-6"
                            onClick={() => handleNavigate(1)}
                            disabled={!rangeStartDateKey}
                            aria-label={`Next ${viewMode === 'calendar' ? 'month' : 'week'}`}
                        >
                            <ChevronRight className="size-3.5" />
                        </Button>
                    </div>
                ) : null}
            </header>
            <div className="space-y-3">
                {!hasIds ? (
                    <div className="text-muted-foreground text-sm">
                        Select a caregiver to view availability.
                    </div>
                ) : isLoading ? (
                    <div className="flex h-32 items-center justify-center">
                        <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                ) : error ? (
                    <div className="py-6">
                        <p className="text-muted-foreground text-sm">
                            Failed to load availability data.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div>
                            {viewMode === 'calendar' &&
                            rangeStartDateKey &&
                            todayKey ? (
                                <AvailabilityMonthGrid
                                    monthStartKey={rangeStartDateKey}
                                    todayKey={todayKey}
                                    timezone={timezone}
                                    dayGroups={timeboxedDayGroups}
                                    hideLegend={true}
                                />
                            ) : viewMode === 'weekly' ? (
                                weeklyItems.length === 0 ? (
                                    <div className="text-muted-foreground text-sm">
                                        No weekly availability patterns set.
                                    </div>
                                ) : (
                                    <WeeklyScheduleList
                                        groups={weeklyDayGroups}
                                        expandedRows={expandedRows}
                                        toggleExpandedRow={toggleExpandedRow}
                                        onEditEntry={handleEditWeekly}
                                        onDeleteEntry={(entry) =>
                                            setDeleteTarget({
                                                type: 'weekly',
                                                id: entry.id,
                                            })
                                        }
                                    />
                                )
                            ) : timeboxedItems.length === 0 ? (
                                <div className="text-muted-foreground text-sm">
                                    No availability or shifts found for this
                                    week.
                                </div>
                            ) : (
                                <TimeboxedScheduleList
                                    groups={timeboxedDayGroups}
                                    expandedRows={expandedRows}
                                    toggleExpandedRow={toggleExpandedRow}
                                    onEditEntry={handleEditOneTime}
                                    onDeleteEntry={(entry) =>
                                        setDeleteTarget({
                                            type: 'one-time',
                                            id: entry.id,
                                        })
                                    }
                                />
                            )}
                        </div>
                        <div className="space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                                {viewMode === 'calendar' ? (
                                    <AvailabilityLegend className="shrink-0" />
                                ) : null}
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="shrink-0"
                                    onClick={handleAdd}
                                >
                                    <Plus className="mr-1 h-4 w-4" />
                                    Add Availability
                                </Button>
                                <Textarea
                                    value={instruction}
                                    onChange={(e) =>
                                        setInstruction(e.target.value)
                                    }
                                    placeholder={
                                        isRecording
                                            ? 'Listening...'
                                            : 'Describe availability changes...'
                                    }
                                    maxLength={500}
                                    disabled={
                                        isParsing ||
                                        isRecording ||
                                        isTranscribing
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            void handleParse();
                                        }
                                    }}
                                    className="min-h-9 min-w-[12rem] flex-1 resize-none"
                                />
                                {canRecordAudio ? (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        className="shrink-0"
                                        aria-label={
                                            isTranscribing
                                                ? 'Transcribing voice input'
                                                : isRecording
                                                  ? 'Stop recording'
                                                  : 'Record voice input'
                                        }
                                        disabled={
                                            isTranscribing ||
                                            (!isRecording && isParsing)
                                        }
                                        onClick={() => {
                                            if (isRecording) {
                                                stopRecording();
                                                return;
                                            }
                                            void startRecording();
                                        }}
                                    >
                                        {isTranscribing ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <span className="relative flex items-center justify-center">
                                                <Mic className="h-4 w-4" />
                                                {isRecording ? (
                                                    <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
                                                ) : null}
                                            </span>
                                        )}
                                    </Button>
                                ) : null}
                                <Button
                                    type="button"
                                    size="icon"
                                    className="shrink-0"
                                    disabled={
                                        isParsing ||
                                        isRecording ||
                                        isTranscribing ||
                                        instruction.trim().length === 0
                                    }
                                    onClick={() => void handleParse()}
                                    aria-label="Parse availability instruction"
                                >
                                    {isParsing ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <SendHorizontal className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                            {voiceInputError ? (
                                <p className="text-destructive text-xs">
                                    {voiceInputError}
                                </p>
                            ) : null}
                        </div>
                    </div>
                )}
            </div>
            {hasIds && organizationId && caregiverId && (
                <AvailabilityFormDialog
                    open={dialogOpen}
                    onOpenChange={setDialogOpen}
                    organizationId={organizationId}
                    caregiverId={caregiverId}
                    timezone={timezone}
                    editTarget={editTarget}
                    onSuccess={() => mutate()}
                />
            )}
            <Dialog
                open={deleteTarget !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setDeleteTarget(null);
                    }
                }}
            >
                <DialogContent showCloseButton={false}>
                    <DialogHeader>
                        <DialogTitle>Delete availability</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this{' '}
                            {deleteTarget?.type === 'weekly'
                                ? 'weekly availability'
                                : 'availability'}{' '}
                            entry? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteTarget(null)}
                            disabled={isDeleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => void handleConfirmDelete()}
                            disabled={isDeleting}
                        >
                            {isDeleting ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : null}
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
}
